import { useClinicianUpgradeModal } from '@/components/modal/clinician-modal/useClinicianUpgradeModal';
import { HeidiUpgradeBaseModal } from '@/components/modal/heidi-upgrade-base-modal/heidi-upgrade-base-modal';
import { PriceDisplay } from '@/components/modal/heidi-upgrade-base-modal/price-display';
import { USAGE_POLICY } from '@/components/utils/legalDocuments';
import { useIsHeidi2PricingPlansModalEnabled } from '@/features/flags';
import { useRegion } from '@/helpers/getRegion';
import { getTermsOfUseLink } from '@/helpers/localiseHelper';
import {
  AppleIapInfoText,
  ClinicianTrialRecapTestimonial,
  clinicianUpgradeModalMessages,
  clinicianUpgradeRecapMessages,
  resolveClinicianTrialRecapTestimonialVariant,
  TeamTrialUpgradeRequestButton,
  trialRecapModalMessages,
  trialUpgradeRequestCallbackModalMessages,
  UpgradeCustomTemplatesSvg,
  UpgradeTermsFooter,
  UpgradeTrialFeatureList,
} from '@heidi/commerce';
import { Period } from '@heidi/commerce/contracts';
import { Button } from '@heidi/design-system/button';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import { TierProFilled } from '@heidi/design-system/icons';
import { Link } from '@heidi/design-system/link';
import { TypographyH2, TypographyP2 } from '@heidi/design-system/typography';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useQueryUser } from '@heidi/workspace/data';
import { Loader2 } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
interface ClinicianUpgradeModalProps {
  launchOpen?: boolean;
  onLaunchClose?: () => void;
}
export const ClinicianUpgradeModal = ({
  launchOpen,
  onLaunchClose,
}: ClinicianUpgradeModalProps = {}) => {
  const {
    isOpen,
    handleOpenChange,
    handleUpgradeClick,
    handleStartTrial,
    handleExtendTrial,
    handleTraineeVerificationClick,
    trialCTAMode,
    isUpgradeDisabled,
    upgradeDisabledTooltip,
    clinicianTrialDaysLeft,
    modalTitleText,
    subtitle,
    modalFeaturesTitle,
    features,
    clinicianProduct,
    isActivateTrialPending,
    isPending,
    showExtendTrialButton,
    handleViewAllPlans,
    showNinetyDayTrialVariant,
    showNonAdminRequestBanner,
    showContactSalesInsteadOfUpgrade,
    handleContactSalesClick,
    hasCallbackRequested,
    isFromTrialRecap,
    isNinetyDayTrialEnded,
    planChangesArticleUrl,
    showTraineeVerificationNudge,
    handleOpenTeamTrialUpgradeRequestModal,
    isOfflineGated,
    isAppleCompliant,
  } = useClinicianUpgradeModal({ launchOpen, onLaunchClose });
  const isHeidi2PricingPlansModalEnabled = useIsHeidi2PricingPlansModalEnabled();
  const intl = useIntl();
  const region = useRegion();
  const { data: user } = useQueryUser();
  const variant = resolveClinicianTrialRecapTestimonialVariant(region, user?.country);
  const termsHref = getTermsOfUseLink(intl.locale);

  return (
    <HeidiUpgradeBaseModal
      hideTestimonial={isFromTrialRecap}
      hidePricing={trialCTAMode || showNonAdminRequestBanner}
      pricingFeaturesGroupedCard={showNinetyDayTrialVariant}
      isUpgradeDisabled={isUpgradeDisabled}
      upgradeDisabledTooltip={upgradeDisabledTooltip}
      showPeriodTabs
      title={
        <div className="flex flex-col gap-2">
          {isFromTrialRecap && <FramedIcon state="pro" className="mx-auto mb-2" />}
          <TypographyH2 className="text-text-primary">
            {isFromTrialRecap ? (
              <FormattedMessage {...clinicianUpgradeRecapMessages.upgradingTitle} />
            ) : isNinetyDayTrialEnded ? (
              <FormattedMessage
                {...clinicianUpgradeRecapMessages.titleNinetyDayTrialEnded}
              />
            ) : showNinetyDayTrialVariant ? (
              <FormattedMessage
                {...clinicianUpgradeRecapMessages.title}
                values={{ daysLeft: clinicianTrialDaysLeft }}
              />
            ) : (
              modalTitleText
            )}
          </TypographyH2>
          {!isFromTrialRecap && (
            <TypographyP2 className="text-text-secondary">
              {isNinetyDayTrialEnded ? (
                <FormattedMessage
                  {...clinicianUpgradeRecapMessages.subtitleNinetyDayTrialEnded}
                />
              ) : showNinetyDayTrialVariant ? (
                <FormattedMessage
                  {...clinicianUpgradeRecapMessages.subtitle}
                  values={{
                    link: (chunks) => (
                      <Link
                        variant="inline"
                        tone="default"
                        href={planChangesArticleUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {chunks}
                      </Link>
                    ),
                  }}
                />
              ) : (
                <>
                  {subtitle}
                  {showTraineeVerificationNudge ? (
                    <>
                      {' '}
                      <FormattedMessage
                        {...clinicianUpgradeModalMessages.traineeVerificationNudge}
                        values={{
                          traineeLink: (chunks) => (
                            <Link
                              href="https://www.heidihealth.com/trainee-sign-up"
                              variant="inline"
                              size="inherit"
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={handleTraineeVerificationClick}
                            >
                              {chunks}
                            </Link>
                          ),
                        }}
                      />
                    </>
                  ) : null}
                </>
              )}
            </TypographyP2>
          )}
        </div>
      }
      featuresTitle={showNinetyDayTrialVariant ? undefined : modalFeaturesTitle}
      features={showNinetyDayTrialVariant ? undefined : features}
      featuresContent={
        showNinetyDayTrialVariant ? (
          <UpgradeTrialFeatureList tier={SubscriptionTier.CLINICIAN_PRO} />
        ) : undefined
      }
      yearlyContent={
        trialCTAMode ? (
          <></>
        ) : clinicianProduct ? (
          <PriceDisplay period={Period.ONE_YEAR} product={clinicianProduct} />
        ) : (
          <></>
        )
      }
      monthlyContent={
        trialCTAMode ? undefined : clinicianProduct ? (
          <PriceDisplay period={Period.ONE_MONTH} product={clinicianProduct} />
        ) : undefined
      }
      rightPanelContent={
        showNinetyDayTrialVariant ? (
          <ClinicianTrialRecapTestimonial variant={variant} />
        ) : (
          <div>
            {/* width=422 matches the intrinsic size of the replaced
                upgrade-custom-templates.svg asset; without it an inline SVG
                falls back to the 300px default in this shrink-to-fit flex item */}
            <UpgradeCustomTemplatesSvg width={422} className="h-auto max-w-full" />
          </div>
        )
      }
      primaryUpgradeButtonContent={
        trialCTAMode ? (
          <>
            <TierProFilled size={16} />
            {isActivateTrialPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              <FormattedMessage {...clinicianUpgradeModalMessages.start14DayTrial} />
            )}
          </>
        ) : (
          <>
            <TierProFilled size={16} />
            {isPending ? (
              <Loader2 className="animate-spin" />
            ) : showNinetyDayTrialVariant ? (
              <FormattedMessage {...clinicianUpgradeModalMessages.upgradeToClinician} />
            ) : (
              <FormattedMessage {...clinicianUpgradeModalMessages.upgradeNow} />
            )}
          </>
        )
      }
      isUpgradePending={trialCTAMode ? isActivateTrialPending : isPending}
      onUpgradeClick={
        trialCTAMode
          ? () => void handleStartTrial()
          : (period) => void handleUpgradeClick(period)
      }
      secondaryButton={
        // Offline the slot is empty, not swapped. Both of its branches open a
        // second billing surface that carries no offline treatment of its own —
        // the gamify trial-extension modal (whose "Keep <plan>" CTA creates a
        // Stripe checkout) and the pricing-plans modal (whose tier CTAs do the
        // same). Suppressing only the first hands the slot to the second, which
        // is a worse outcome than leaving both, so the guard sits on the slot.
        isOfflineGated ? undefined : showExtendTrialButton ? (
          <Button
            variant="outline"
            size="md"
            className="w-full"
            onClick={handleExtendTrial}
          >
            <FormattedMessage {...clinicianUpgradeModalMessages.extendYourTrial} />
          </Button>
        ) : isHeidi2PricingPlansModalEnabled ? (
          <Button
            variant="ghost"
            size="md"
            className="w-full"
            onClick={handleViewAllPlans}
          >
            <FormattedMessage {...clinicianUpgradeModalMessages.viewAllPlans} />
          </Button>
        ) : undefined
      }
      footerText={
        showNonAdminRequestBanner ? undefined : showNinetyDayTrialVariant ? (
          <UpgradeTermsFooter termsHref={termsHref} privacyHref={USAGE_POLICY} />
        ) : trialCTAMode ? (
          <FormattedMessage {...clinicianUpgradeModalMessages.trialCTAFooterText} />
        ) : (
          <FormattedMessage {...clinicianUpgradeModalMessages.cancelAnytime} />
        )
      }
      footerCaptionClassName={
        showNinetyDayTrialVariant && !showNonAdminRequestBanner
          ? 'text-center'
          : undefined
      }
      primaryCtaReplacement={
        isAppleCompliant
          ? () => <AppleIapInfoText className="text-center text-text-secondary" />
          : // Offline, fall back to the base modal's own primary button so the
            // "Not available offline" disabled treatment is what the user sees,
            // rather than a live team-request / contact-sales replacement.
            isOfflineGated
            ? undefined
            : showNonAdminRequestBanner
              ? (assignPrimaryButtonRef) => (
                  <TeamTrialUpgradeRequestButton
                    ref={(el) => {
                      assignPrimaryButtonRef(el);
                    }}
                    plan={SubscriptionTier.CLINICIAN_PRO}
                    onOpenTeamRequestModal={handleOpenTeamTrialUpgradeRequestModal}
                  />
                )
              : showContactSalesInsteadOfUpgrade
                ? (assignPrimaryButtonRef) => (
                    <Button
                      ref={(el) => {
                        assignPrimaryButtonRef(el);
                      }}
                      type="button"
                      variant="pro"
                      size="md"
                      disabled={hasCallbackRequested}
                      onClick={handleContactSalesClick}
                      className="w-full"
                    >
                      {!hasCallbackRequested && <TierProFilled size={16} />}
                      <FormattedMessage
                        {...(hasCallbackRequested
                          ? trialUpgradeRequestCallbackModalMessages.callbackRequestedCta
                          : trialRecapModalMessages.talkToUsKeepPlanCtaClinician)}
                      />
                    </Button>
                  )
                : undefined
      }
      open={isOpen}
      onOpenChange={handleOpenChange}
      primaryButtonTestId="billing-clinician-upgrade-confirm-button"
    />
  );
};
